import type {
  NonExtraFacilityCategoryKey,
  ParsedFacilityItem,
} from "@/features/hotels/utils/facilities/types";

export const normalizeFacilityLabel = (label: string): string =>
  label.trim().replace(/\s+/g, " ").toLowerCase();

const API_CATEGORY_PREFIX_MAP: Record<string, NonExtraFacilityCategoryKey> = {
  "core essentials": "core-essentials",
  accessibility: "accessibility",
  safety: "safety",
  leisure: "leisure",
  dining: "dining",
  catering: "dining",
  service: "service",
  sports: "sports",
  "core support": "core-support",
  wellness: "wellness",
  health: "wellness",
  family: "family",
  business: "business",
  entertainment: "entertainment",
  secondary: "secondary",
  policies: "policies",
  admin: "admin",
};

export const parseCategoryPrefixedLabel = (
  rawLabel: string,
): {
  strippedLabel: string;
  categoryHint?: NonExtraFacilityCategoryKey;
  value?: number;
} => {
  const colonIndex = rawLabel.indexOf(":");
  if (colonIndex <= 0) {
    return { strippedLabel: rawLabel };
  }

  const prefix = rawLabel.slice(0, colonIndex).trim();
  const remainder = rawLabel.slice(colonIndex + 1).trim();

  if (!remainder) {
    return { strippedLabel: rawLabel };
  }

  const normalizedPrefix = normalizeFacilityLabel(prefix).replace(/-/g, " ");
  const categoryHint = API_CATEGORY_PREFIX_MAP[normalizedPrefix];

  if (!categoryHint) {
    if (/^\d+(\.\d+)?$/.test(remainder)) {
      return { strippedLabel: prefix, value: Number(remainder) };
    }
    return { strippedLabel: rawLabel };
  }

  return { strippedLabel: remainder, categoryHint };
};

export const parseHotelFacilities = (
  facilities: string | null | undefined,
): ParsedFacilityItem[] => {
  if (!facilities || facilities.trim().length === 0) {
    return [];
  }

  const uniqueByNormalized = new Map<string, ParsedFacilityItem>();

  facilities.split(",").forEach((rawPart) => {
    const rawLabel = rawPart.trim();
    if (!rawLabel) {
      return;
    }

    const {
      strippedLabel: originalLabel,
      categoryHint,
      value,
    } = parseCategoryPrefixedLabel(rawLabel);

    const normalizedLabel = normalizeFacilityLabel(originalLabel);
    const dedupeKey = `${categoryHint ?? "unmapped"}::${normalizedLabel}`;

    if (!normalizedLabel || uniqueByNormalized.has(dedupeKey)) {
      return;
    }

    uniqueByNormalized.set(dedupeKey, {
      originalLabel,
      normalizedLabel,
      categoryHint,
      value: value,
    });
  });

  return [...uniqueByNormalized.values()];
};
